import { api } from "./api";
import { decimalString, decimalUnits } from "./receivedInvoices";
import { centsString } from "./invoiceCosts";

export interface ExpenseOptions {
  sites: { id: string; naziv: string; mjesto_troska: string | null }[];
  recipients: { id: string; naziv: string }[];
  konto_obveze: string;
  konto_troska: string;
}
export interface ExpenseFields {
  trgovina: string;
  datum: string;
  iznos: string;
  pdv: string;
}
export interface ExpenseInput extends ExpenseFields {
  gradiliste_id: string;
  platio: "radnik" | "kartica";
  povrat_korisnik_id: string | null;
  opis: string;
  dokument_id: string;
}
export interface Expense extends ExpenseInput {
  id: string;
  unio_korisnik_id: string;
  konto_id: string;
  trosak_konto_id: string;
  trosak_iznos: string;
  stanje_povrata: string;
}
export interface ReceiptReading {
  dokument_id: string;
  fields: { trgovina: string | null; datum: string | null; iznos: string | null; pdv: string | null };
  warning: string | null;
}
export interface ExpenseListItem {
  id: string;
  version: number;
  datum: string;
  trgovina: string;
  gradiliste_id: string;
  gradiliste: string;
  bruto: string;
  pdv: string;
  neto: string;
  primatelj_povrata: string | null;
  unio: string;
  stanje_povrata: "nije_potrebno" | "za_povrat" | "isplaceno";
}
export interface ExpenseDetail extends ExpenseListItem {
  opis: string;
  platio: "radnik" | "kartica";
  dokument_id: string;
  povrat_isplacen_at: string | null;
}
export const expensePath = "/nabava/ulazni-racuni/bez-narudzbe";
const expenseApi = "/nabava/troskovi-bez-narudzbe";
export const refundLabels = {
  nije_potrebno: "Povrat nije potreban",
  za_povrat: "Za povrat radniku",
  isplaceno: "Povrat isplaćen",
};
export function expenseNet(gross: string, vat: string) {
  const g = decimalUnits(gross),
    v = decimalUnits(vat);
  return g === null || g <= 0n || v === null || v > g ? null : centsString(g - v);
}
export function receiptFieldsReason(fields: ExpenseFields) {
  if (!fields.trgovina.trim()) return "Upišite trgovinu s računa.";
  const date = new Date(`${fields.datum}T12:00:00Z`);
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(fields.datum) ||
    !Number.isFinite(date.getTime()) ||
    date.toISOString().slice(0, 10) !== fields.datum ||
    fields.datum.startsWith("0000")
  )
    return "Upišite valjani datum računa.";
  if (decimalUnits(fields.iznos) === null || decimalUnits(fields.iznos) === 0n)
    return "Upišite pozitivan bruto iznos s najviše dvije decimale.";
  if (decimalUnits(fields.pdv) === null) return "Upišite nenegativan PDV s najviše dvije decimale.";
  if (expenseNet(fields.iznos, fields.pdv) === null) return "PDV ne može biti veći od iznosa računa.";
  return "";
}
export const expenseReceipts = {
  list: async (refundOnly: boolean, signal?: AbortSignal) =>
    (await api.get<ExpenseListItem[]>(expenseApi, { params: refundOnly ? { za_povrat: true } : {}, signal })).data,
  detail: async (id: string, signal?: AbortSignal) =>
    (await api.get<ExpenseDetail>(`${expenseApi}/${id}`, { signal })).data,
  photo: async (id: string, signal?: AbortSignal) =>
    (await api.get<Blob>(`${expenseApi}/${id}/fotka`, { responseType: "blob", signal })).data,
  markRefundPaid: async (id: string, version: number) =>
    (await api.post<ExpenseDetail>(`${expenseApi}/${id}/povrat-isplacen`, { expected_version: version })).data,
  options: async (datum: string, signal?: AbortSignal) =>
    (await api.get<ExpenseOptions>("/nabava/troskovi-bez-narudzbe/opcije", { params: { datum }, signal })).data,
  read: async (site: string, file: File, signal?: AbortSignal) => {
    const data = new FormData();
    data.set("gradiliste_id", site);
    data.set("fotka", file);
    return (await api.post<ReceiptReading>("/nabava/troskovi-bez-narudzbe/procitaj-racun", data, { signal })).data;
  },
  send: async (input: ExpenseInput) =>
    (
      await api.post<Expense>("/nabava/troskovi-bez-narudzbe", {
        ...input,
        trgovina: input.trgovina.trim(),
        opis: input.opis.trim(),
        iznos: decimalString(input.iznos),
        pdv: decimalString(input.pdv),
        povrat_korisnik_id: input.platio === "radnik" ? input.povrat_korisnik_id : null,
      })
    ).data,
};
